<template>
	<form
		id="contact-form"
		@submit.prevent="submit"
	>
		<div class="">
			<div class="py-12 text-center md:pb-8">
				<h2 class="pb-4 text-3xl font-bold text-center">
					{{ $t('contact.form.h2') }}
				</h2>
				<p class="max-w-2xl pb-4 mx-auto text-xl">
					{{ $t('contact.form.subtitle') }}
				</p>
			</div>
			<span class="pt-4 pb-2 text-lg font-semibold md:py-8">
				{{ $t('contact.form.your-information') }}
			</span>
			<div class="grid grid-cols-1 py-4 gap-x-12 gap-y-4 md:grid-cols-2">
				<FormTextInput
					:id="$t('contact.form.first-name.id')"
					v-model="firstName"
					floating-label
					:label="$t('contact.form.first-name.label')"
					:placeholder="$t('contact.form.first-name.label')"
					:autocomplete="FIRST_NAME_AUTOCOMPLETE"
					:error-message="$t('first-name-error-message')"
					:state="inputFieldStateValidator(firstName, TEXT)"
					required
				/>
				<FormTextInput
					:id="$t('contact.form.last-name.id')"
					v-model="lastName"
					floating-label
					:label="$t('contact.form.last-name.label')"
					:placeholder="$t('contact.form.last-name.label')"
					:autocomplete="LAST_NAME_AUTOCOMPLETE"
					:error-message="$t('last-name-error-message')"
					:state="inputFieldStateValidator(lastName, TEXT)"
					required
				/>
				<FormTextInput
					:id="$t('contact.form.email.id')"
					v-model="email"
					floating-label
					:label="$t('contact.form.email.label')"
					:placeholder="$t('contact.form.email.label')"
					:autocomplete="EMAIL_AUTOCOMPLETE"
					:error-message="$t('address-error-message')"
					:state="inputFieldStateValidator(email, EMAIL)"
					required
				/>
				<FormTextInput
					:id="$t('contact.form.phone-number.id')"
					v-model="phone"
					floating-label
					:label="$t('contact.form.phone-number.label')"
					:placeholder="$t('contact.form.phone-number.label')"
					:autocomplete="PHONE_AUTOCOMPLETE"
					:input-type="TEL"
					:error-message="$t('phone-error-message')"
					required
					:state="formPhoneInputState"
					@updated="handlePhoneValidation"
				/>
				<FormTextInput
					:id="$t('contact.form.address.id')"
					v-model="address"
					floating-label
					:label="$t('contact.form.address.label')"
					:placeholder="$t('contact.form.address.label')"
					:autocomplete="STREET_ADDRESS_AUTOCOMPLETE"
					:error-message="$t('address-error-message')"
					:state="inputFieldStateValidator(address, TEXT)"
					required
				/>
				<FormTextInput
					:id="$t('contact.form.city.id')"
					v-model="city"
					floating-label
					:label="$t('contact.form.city.label')"
					:placeholder="$t('contact.form.city.label')"
					:autocomplete="CITY_AUTOCOMPLETE"
					:error-message="$t('city-error-message')"
					:state="inputFieldStateValidator(city, TEXT)"
					required
				/>
				<FormSelectInput
					:id="$t('contact.form.state.id')"
					v-model="geographicState"
					:options="STATES"
					:label="$t('contact.form.state.label')"
					:aria-label="$t('aria.select-state')"
					:placeholder="$t('contact.form.state.placeholder')"
					required
					:error-message="$t('geographic-state-error-message')"
					:state="inputFieldStateValidator(geographicState, TEXT)"
				/>
				<FormTextInput
					:id="$t('contact.form.zip.id')"
					v-model="zip"
					floating-label
					:label="$t('contact.form.zip.label')"
					:placeholder="$t('contact.form.zip.label')"
					:autocomplete="ZIP_CODE_AUTOCOMPLETE"
					:error-message="$t('zip-code-error-message')"
					:state="inputFieldStateValidator(zip, ZIP)"
					required
				/>
			</div>
			<div class="w-full md:col-span-2">
				<label class="pt-4 pb-2 text-lg font-semibold lg:py-8">
					{{ $t('contact.form.message-title') }}
				</label>
				<span class="text-sm text-red-500 lowercase">
					*
				</span>
				<FormTextInput
					:id="$t('contact.form.message.id')"
					v-model="message"
					hide-label
					:label="$t('contact.form.message.label')"
					placeholder=""
					rows="10"
					:input-type="TEXTAREA"
					:error-message="$t('textarea-error-message')"
					required
					:state="inputFieldStateValidator(message, TEXTAREA)"
					input-class="w-full"
				/>
			</div>
			<div class="flex justify-center py-4">
				<FormButton
					form="contact-form"
					button-classes="max-w-sm"
					button-height="h-14"
					submit
					:disabled="false"
					:is-loading="loading"
					uppercase
				>
					{{ $t('contact.form.button-text') }}
				</FormButton>
			</div>
		</div>
	</form>
</template>

<script setup lang="ts">
import { AsYouType } from 'libphonenumber-js'
import { inputFieldStateValidator } from '@/validators/text-inputs.js'
import { TEXT, TEXTAREA, EMAIL, TEL, ZIP } from '@/constants/form/text-inputs/types.js'
import { ERROR, SUCCESS } from '@/constants/form/states.js'
import {
	STREET_ADDRESS_AUTOCOMPLETE,
	FIRST_NAME_AUTOCOMPLETE,
	LAST_NAME_AUTOCOMPLETE,
	PHONE_AUTOCOMPLETE,
	EMAIL_AUTOCOMPLETE,
	CITY_AUTOCOMPLETE,
	ZIP_CODE_AUTOCOMPLETE
} from '@/constants/form/autocompletes.js'
import { STATES } from '@/constants/states.js'
import { API_BASE, API_ENDPOINT_NON_BUSINESS_CONTACT } from '@/constants/index.js'
import { CONTACT_FORM_SUBMIT } from '@/constants/gtm/events.js'
import type { ContactFormSubmitEvent } from '@/types/gtm/events'
import { useTrackEvent } from '@/composables/useEventTracking'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useToast } from '@/composables/useToast'
const { addToast } = useToast()

const { reportError } = useErrorReporter(useBugsnag().notify)

const { t } = useI18n({
	useScope: 'global'
})

const firstName = ref('')
const lastName = ref('')
const address = ref('')
const email = ref('')
const phone = ref('')
const phoneValid = ref(false)
const message = ref('')
const city = ref('')
const geographicState = ref('')
const zip = ref('')
const loading = ref(false)

const emit = defineEmits([ 'submitResponse' ])

const handlePhoneValidation = (payload: AsYouType) => {
	phoneValid.value = payload.isValid()
}
const formPhoneInputState = computed(() => {
	if (!phone.value.length) {
		return ERROR
	}
	return phoneValid.value ? SUCCESS : ERROR
})

const formDisabled = computed(() => {
	return !(
		firstName.value.length	&&
		lastName.value.length	&&
		inputFieldStateValidator(email.value, EMAIL) === SUCCESS &&
		formPhoneInputState.value === SUCCESS &&
		address.value.length &&
		city.value.length &&
		geographicState.value.length &&
		zip.value.length &&
		message.value.length
	)
})

const formData = computed(() => {
	return {
		firstName: firstName.value,
		lastName: lastName.value,
		email: email.value,
		phone: phone.value,
		address: address.value,
		city: city.value,
		state: geographicState.value,
		zip: zip.value,
		message: message.value
	}
})

const resetForm = () => {
	firstName.value = ''
	lastName.value = ''
	email.value = ''
	phone.value = ''
	address.value = ''
	city.value = ''
	geographicState.value = ''
	zip.value = ''
	message.value = ''
}

const submit = async () => {
	const trackedEvent: ContactFormSubmitEvent = {
		event: CONTACT_FORM_SUBMIT,
		category: 'Contact',
		action: 'click',
		label: 'Contact Form Submit',
		first_name: firstName.value,
		last_name: lastName.value,
		address: address.value,
		email: email.value,
		phone: phone.value,
		message: message.value,
		city: city.value,
		state: geographicState.value,
		zip: zip.value
	}
	useTrackEvent(trackedEvent)
	if (formDisabled.value) {
		addToast({
			title: t('contact.form.error-toast.title'),
			message: t('contact.form.error-toast.message')
		})
		return
	}

	const { pending } = await useFetch(`${API_BASE}${API_ENDPOINT_NON_BUSINESS_CONTACT}`, {
		method: 'POST',
		body: formData.value,
		onRequestError ({ error }) {
			const errorMessage = error?.message || 'Error submitting contact form'
			reportError(errorMessage, true, {
				title: t('contact.form.error-toast.title'),
				message: t('contact.form.error-toast.message')
			})
		},
		onResponse ({ response }) {
			if (response.status === 200) {
				resetForm()
				emit('submitResponse', SUCCESS)
			}
		},
		onResponseError ({ response }) {
			const errorMessage = response._data?.errors[0]?.title || 'Error submitting contact form'
			reportError(errorMessage, true, {
				title: response._data?.errors[0]?.title || 'Error submitting contact form',
				message: response._data?.errors[0]?.detail || response._data?.error
			})
		}
	})
	loading.value = pending.value
}
</script>
